<template>
  <b-nav-item-dropdown
    ref="notifyDropdown"
    class="dropdown-notification mr-25"
    menu-class="dropdown-menu-media notify-dropdown"
    @show="openList"
    @hide="hideList"
    right
  >
    <template #button-content>
      <feather-icon
        :badge="totalSeen > 0 ? totalSeen : ''"
        badge-classes="bg-danger"
        class="text-body"
        icon="BellIcon"
        size="21"
      />
    </template>

    <!-- Header -->
    <li class="dropdown-menu-header">
      <div class="dropdown-header">
        <h4 class="notification-title mb-0 mr-auto">Notifications</h4>
        <b-button
          v-if="notifies.length > 0"
          @click.stop="clearAll"
          variant="flat-danger"
          size="sm"
        >
          Clear All
        </b-button>
        <b-badge v-if="totalSeen > 0" pill variant="light-primary" class="mx-50">
          {{ totalSeen }} NEW
        </b-badge>
        <b-button
          @click.stop="closeDropdown"
          variant="flat-secondary"
          size="sm"
          class="close-x-btn"
        >
          <feather-icon icon="XIcon" size="20" />
        </b-button>
      </div>
    </li>

    <!-- Notifications -->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="scrollable-container media-list scroll-area"
      tagname="li"
    >
      <div
        class="notify-card"
        :class="{ 'notify-card--new': !n.is_seen }"
        v-for="(n, idx) in notifies"
        :key="`notify${idx}`"
      >
        <div class="notify-card__header">
          <div class="notify-card__icon">
            <feather-icon icon="BellIcon" size="16" />
          </div>
          <h5 class="notify-card__title" v-text="n.notification.title" />
          <b-badge v-if="!n.is_seen" variant="primary" class="notify-card__badge">
            NEW
          </b-badge>
        </div>
        <div
          class="notify-card__body"
          v-html="n.notification.description"
        />
      </div>

      <!-- Empty state -->
      <div v-if="notifies.length === 0" class="notify-empty">
        <feather-icon icon="InboxIcon" size="40" class="mb-75" />
        <p class="notify-empty__title">No notifications</p>
        <p class="notify-empty__subtitle">You're all caught up!</p>
      </div>
    </vue-perfect-scrollbar>

    <!-- Footer close button -->
    <li class="dropdown-menu-footer">
      <b-button
        @click.stop="closeDropdown"
        variant="outline-secondary"
        block
        class="close-btn"
      >
        <feather-icon icon="XIcon" size="16" class="mr-50" />
        Close Notifications
      </b-button>
    </li>
  </b-nav-item-dropdown>
</template>

<script>
import {
  BNavItemDropdown,
  BBadge,
  BMedia,
  BLink,
  BAvatar,
  BButton,
  BFormCheckbox,
} from "bootstrap-vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import Ripple from "vue-ripple-directive";
import { mapState, mapGetters, mapMutations } from "vuex";
import Notify from "@/services/notification.service.js";

export default {
  data: () => ({
    notifies: [],
    totalSeen: 0,
  }),
  directives: { Ripple },
  async mounted() {
    await this.load();

      // Auto-open dropdown if there are unread notifications on page load
    if (this.totalSeen > 0) {
      this.$nextTick(() => {
        if (this.$refs.notifyDropdown) {
          this.$refs.notifyDropdown.show();
        }
      });
    }

    const notifyEvent = (userId = "") =>
      this.$pusher
        .subscribe(`NotifyChannel${userId}`)
        .bind(
          `NotifyEvent`,
          async ({ title, description, updatedCredit = null }) => {
            await this.load();

            this.$nextTick(() => {
              if (this.$refs.notifyDropdown) {
                this.$refs.notifyDropdown.show();
              }
            });

            if (updatedCredit) {
              this.SET_CREDIT(updatedCredit);
            }
          }
        );
    notifyEvent();
    notifyEvent(this.userID);
  },
  destroyed() {
    this.$pusher.unsubscribe("NotifyChannel");
  },
  computed: {
    ...mapGetters(["isAdmin"]),
    ...mapState({ user: "user" }),
    userID() {
      return this.user?.user.id;
    },
  },
  methods: {
    ...mapMutations(["SET_CREDIT"]),
    closeDropdown() {
      if (this.$refs.notifyDropdown) {
        this.$refs.notifyDropdown.hide();
      }
    },
    hideList() {
      this.load();
    },
    async openList() {
      await Notify.readAll();
    },
    async load() {
      const { list, totalSeen } = await Notify.my();
      this.notifies = list;
      this.totalSeen = totalSeen;
    },
    async clearAll() {
      await Notify.clearAll();
      await this.load();
    },
  },
  setup() {
    return {
      perfectScrollbarSettings: {
        maxScrollbarLength: 60,
        wheelPropagation: false,
      },
    };
  },
  components: {
    BNavItemDropdown,
    BBadge,
    BMedia,
    BLink,
    BAvatar,
    VuePerfectScrollbar,
    BButton,
    BFormCheckbox,
  },
};
</script>

<style lang="scss" scoped>
.dropdown-header {
  display: flex;
  align-items: center;
}

.close-x-btn {
  width: 34px;
  height: 34px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-left: 4px;
}

/* ─── Notification card ─── */
.notify-card {
  margin: 10px 12px;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.01);
  cursor: default;
  transition: all 0.2s ease;

  &:first-child {
    margin-top: 12px;
  }

  &:last-child {
    margin-bottom: 12px;
  }

  &:hover {
    border-color: rgba(0, 0, 0, 0.12);
  }

  /* New / unread notification */
  &--new {
    border-color: rgba(115, 103, 240, 0.35);
    background: rgba(115, 103, 240, 0.06);
    box-shadow: 0 2px 8px rgba(115, 103, 240, 0.1);

    .notify-card__icon {
      background: #7367f0;
      color: #fff;
    }

    .notify-card__title {
      color: #7367f0;
    }

    &:hover {
      border-color: rgba(115, 103, 240, 0.5);
    }
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  &__icon {
    width: 30px;
    height: 30px;
    min-width: 30px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    flex: 1;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.3;
    margin: 0;
  }

  &__badge {
    flex-shrink: 0;
    font-size: 11px;
  }

  &__body {
    font-size: 14px;
    line-height: 1.6;
    word-break: break-word;
  }
}

/* ─── Separator between new and old ─── */
.notify-card--new + .notify-card:not(.notify-card--new) {
  margin-top: 18px;

  &::before {
    content: '';
    display: block;
    height: 1px;
    background: rgba(0, 0, 0, 0.08);
    margin: -14px -16px 14px;
    width: calc(100% + 32px);
  }
}

/* ─── Empty state ─── */
.notify-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  opacity: 0.45;

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }

  &__subtitle {
    margin: 4px 0 0;
    font-size: 13px;
  }
}

/* ─── Footer ─── */
.close-btn {
  font-size: 14px;
  font-weight: 600;
  padding: 12px;
}
</style>

<!-- Unscoped: dropdown width + dark theme overrides -->
<style lang="scss">
.notify-dropdown.dropdown-menu-media {
  width: 22rem;

  @media (min-width: 576px) {
    width: 28rem;
  }

  @media (min-width: 768px) {
    width: 32rem;
  }

  .media-list {
    max-height: 28rem;
  }
}

/* ─── Dark theme ─── */
.dark-layout .dropdown-notification {
  .notify-card {
    border-color: rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.04);

    &:hover {
      border-color: rgba(255, 255, 255, 0.2);
    }

    &--new {
      border-color: rgba(115, 103, 240, 0.45);
      background: rgba(115, 103, 240, 0.12);

      &:hover {
        border-color: rgba(115, 103, 240, 0.6);
      }
    }

    &__header {
      border-bottom-color: rgba(255, 255, 255, 0.08);
    }

    &__icon {
      background: rgba(255, 255, 255, 0.12);
      color: #d0d2d6;
    }

    &__title {
      color: #d0d2d6;
    }

    &__body {
      color: #b4b7bd;
    }
  }

  /* Separator */
  .notify-card--new + .notify-card:not(.notify-card--new)::before {
    background: rgba(255, 255, 255, 0.08);
  }
}
</style>
